import { Badge, Divider, Grid, Typography } from '@material-ui/core';
import AccountTree from '@material-ui/icons/AccountTree';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Code from '@material-ui/icons/Code';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Help from '@material-ui/icons/Help';
import OpenInNew from '@material-ui/icons/OpenInNew';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import ShortText from '@material-ui/icons/ShortText';

import { TreeItem, TreeView } from '@material-ui/lab';
import { createStyles, withStyles } from '@material-ui/styles';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';

import { formApi, formatLocalDate, getCurrentVisitSession, log, showToast, withTooltip } from '..';
import { ReportBug } from '../../components/common/navigation';
import { FieldValue } from '../../components/common/presentation';

import { Auto } from '../controls';
import { Visit, WorksheetSummary } from '../domain';
import { HBox, TreeProps, VBox } from '../layout';

import { Page } from '../page';

import { mediumGray, rootStyle } from '../theme';
import { DocumentApi, clearDocuments, documentApi2 } from './document-api';
import { DocumentLibrary } from './document-library/document-library';
import { DocumentRenderer } from './document-renderer';
import { BundleVersion, DocumentBundle, DocumentRef, KnownDocumentFormats } from './document-types';
import { DocumentNode, documentBundleTreeProps, getAllDocumentIds, getFirstDocument, getFirstLeaf } from './document-utils';


const badgeSize = 8;
const DocumentIconBadge: any = withStyles(() =>
  createStyles({
    badge: {
      right: 3,
      top: 3,
      padding: 0,
      width: badgeSize,
      height: badgeSize,
      maxWidth: badgeSize,
      maxHeight: badgeSize,
      border: '1px solid white',
      backgroundColor: (props) => (props && props.backgroundcolor) || mediumGray,
    },
  })
)(Badge);

const defaultTreeStyles = {
  root: {
    height: 216,
    flexGrow: 1,
    maxWidth: 390,
  },
};

const openRaw = false;

function addReadBadge(icon: any, hasUpdate = false, tooltip = null, onOpenDoc?: () => void) {
  const height = 30;
  const a = (
    <div style={{ paddingTop: 1, height, maxHeight: height }}>
      {hasUpdate ? (
        <DocumentIconBadge badgeContent=" " variant="dot" backgroundcolor="red">
          {icon}
        </DocumentIconBadge>
      ) : (
        icon
      )}
    </div>
  );

  const onOpen = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (onOpenDoc) {
      onOpenDoc();
    }
  };

  const openLink = <OpenInNew onClick={onOpen} style={{ marginTop: 5 }} fontSize="small" htmlColor="gray" />;

  return (
    <Grid container direction="row" alignContent="center" justifyContent="flex-start" style={{ flexWrap: 'nowrap' }}>
      <Grid item>{a}</Grid>
      <Grid item style={{ flexGrow: 1 }} />
      {openRaw && (
        <Grid item style={{ paddingRight: 12 }}>
          {tooltip ? withTooltip(tooltip, openLink) : openLink}
        </Grid>
      )}
    </Grid>
  );
}

export const documentNodeLabel = (a: DocumentNode) => {
  const format: KnownDocumentFormats = a?.data?.format;

  if (!format) {
    return a.label;
  }
  const showBadge = !a.data.readByUser;
  const tooltip = a.data.key;
  const style = { paddingTop: 4 };

  const onOpen = () => documentApi2.openInTab(a.data);

  switch (format) {
    case 'txt':
      return addReadBadge(<ShortText style={style} />, showBadge, tooltip, onOpen);
    case 'pdf':
    case 'PDF':
      return addReadBadge(<PictureAsPdf style={style} />, showBadge, tooltip, onOpen);
    case 'html':
      return addReadBadge(<Code style={style} />, showBadge, tooltip);
    case 'json':
      return addReadBadge(<AccountTree style={style} />, showBadge, tooltip, onOpen);
    default:
      return addReadBadge(<Help style={style} />, showBadge, tooltip, onOpen);
  }
};

export const onDocumentNodeSelected = (fn: (a: DocumentRef, parent?: DocumentNode) => void) => (a: DocumentNode) => {
  if (!a?.data?.key) {
    return;
  }
  a.data.readByUser = true;
  fn(a.data, a);
};

type BundleVersionsProps = {
  bundle: DocumentBundle;
  onVersionSelected?: (a: BundleVersion) => void;
};

type SummaryListProps = {
  summaries: WorksheetSummary[];
  onSelected?: (a: WorksheetSummary) => void;
  selected?: WorksheetSummary;
  worksheetId?: string;
};

function SummaryList({ summaries, onSelected, selected, worksheetId }: SummaryListProps) {
  const [currentSummary, setCurrentSummary] = useState(selected || summaries[0]);

  const summary = documentApi2.data((s) => s.summary) || selected;
  const form = useRef(
    formApi({
      values: { summary: currentSummary },
      fields: {
        summary: {
          style: { width: 200 },
          disableClearable: true,
          list: summaries,
          listLabel: (b: WorksheetSummary) => (b.worksheetId === worksheetId ? '* ' : '') + b.worksheetType + ' - ' + formatLocalDate(b.serviceDate),
          onChange: (v) => {
            if (v) {
              setCurrentSummary(v);
              if (onSelected) {
                onSelected(v);
              }
            }
          },
        },
      },
    })
  ).current;

  useEffect(() => {
    if (summary) {
      form.controls.summary.value.setState({ value: summary });
    }
  }, [summary]);

  return <Auto {...form.controls.summary} />;
}

function DocumentTree<T = any>(props: TreeProps<T>) {
  const { style, data, id, label, children, firstDocument } = props;
  const classes = rootStyle(defaultTreeStyles, style)();

  const [expanded, setExpanded] = useState(props.expanded || []);

  const first = (firstDocument && firstDocument(data as any)) || getFirstLeaf(data[0]);
  const [selected, setSelected] = useState(first?.id ? [first.id] : []);
  const map = new Map<string, T>();

  const onToggle = (_event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const onSelect = (_, id) => {
    setSelected(id);

    if (props.onSelected) {
      props.onSelected(map.get(id as string));
    }
  };

  const renderTree = (n: T | T[]) => {
    if (Array.isArray(n)) {
      return (n as any).map((v: T) => renderTree(v));
    }
    const key = id ? id(n) + '' : '';
    if (map.get(key)) {
      return null;
    }
    map.set(key, n);
    const nodes = children(n);
    return (
      <TreeItem role={'tree_item'} id={key} key={key} nodeId={key} label={label(n)}>
        {Array.isArray(nodes) ? nodes.map((v) => renderTree(v)) : null}
      </TreeItem>
    );
  };

  useEffect(() => {
    if (first) {
      onSelect(null, first?.id);
    }
  }, []);

  const bundleChanged = documentApi2.data((s) => s.bundle);
  const show = clearDocuments((s) => s.show);

  useEffect(() => {
    const ids = getAllDocumentIds(data as any);
    setExpanded(ids.concat(ids));
  }, [bundleChanged]);

  const tree = renderTree(data);

  return (
    show &&
    bundleChanged && (
      <TreeView
        className={classes.root}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpanded={expanded}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        selected={selected}
        onNodeToggle={onToggle}
        onNodeSelect={onSelect}
        role="tree_view"
      >
        {tree}
      </TreeView>
    )
  );
}

type DocumentNavigationProps = {
  bundle?: DocumentBundle;
  onDocumentSelected?: (a: DocumentRef, parent?: DocumentNode) => void;
  onVersionSelected?: (a: BundleVersion) => void;
};

export function DocumentNavigation({ bundle, onDocumentSelected }: DocumentNavigationProps) {


  const treeProps = documentBundleTreeProps({
    bundle,
    label: documentNodeLabel,
  });

  const onSelected = onDocumentNodeSelected(onDocumentSelected);
  const visit = documentApi2.data((s) => s.visit);
  const summaries = visit?.wsSummaries;

  const { bundleVersionForm } = documentApi2;

  useEffect(() => {
    const data = treeProps?.data;
    const first = (data && getFirstDocument(visit.locationCode, data as any)) || getFirstLeaf(data[0]);
    if (first) {
      onDocumentSelected(first.data, first);
    }
  }, [bundle?.bundleMasterId]);
  return (
    <>
      {summaries?.length > 1 ? (
        <SummaryList
          worksheetId={visit?.worksheetId}
          selected={visit?.worksheetId ? summaries.filter((a) => a.worksheetId === visit.worksheetId)[0] || summaries[0] : summaries[0]}
          summaries={summaries}
          onSelected={(a) => documentApi2.setSummary(a)}
        />
      ) : null}
      <span role="version_controller">
        <Auto {...bundleVersionForm.controls.bundle} />
      </span>
      <br />
      {treeProps?.data ? (
        <DocumentTree {...treeProps} firstDocument={(a) => getFirstDocument(visit.locationCode, a)} onSelected={onSelected} expanded={treeProps.expanded} />
      ) : (
        <div>Bundle Not Available</div>
      )}
    </>
  );
}

function DocumentHeader() {
  return (
    <Grid container style={{ width: '100%' }} justify="center" alignItems="center">
      <Grid item>
        <Typography variant="h6">Documents</Typography>
      </Grid>
      <Grid item style={{ flexGrow: 1 }}></Grid>
      <Grid item>
        <ReportBug />
      </Grid>
    </Grid>
  );
}

type DocumentSideDrawerProps = {
  visit: Visit;
  api: DocumentApi;
  bundle?: DocumentBundle;
};

function BundleView({ api, bundle }) {
  const onDocumentSelected = (a: DocumentRef, b: DocumentNode) => {
    a.category = b?.label;
    api.getContent(a);
  };
  const onVersionSelected = (a: BundleVersion) => {
    api.getBundleVersion(a);
  };

  return (
    <div
      style={{
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginRight: 12,
      }}
      role="document_navigation"
    >
      {bundle && <DocumentNavigation bundle={bundle} onDocumentSelected={onDocumentSelected} onVersionSelected={onVersionSelected} />}
    </div>
  );
}

function DocumentSideDrawer({ visit, api }: DocumentSideDrawerProps) {
  const { patient, accountNumber, mrn, locationCode, workflow, wsSummaries } = visit;
  const bundle = api.data((s) => s.bundle);
  const summary = api.data((s) => s.summary);
  const showDocs = clearDocuments.getState().show;

  const departmentCode = summary?.departmentCode || wsSummaries?.[0]?.departmentCode || visit.departmentCode;
  
  useEffect(() => {
    if (bundle && showDocs) {
      documentApi2.validateAccount(bundle, visit);
    }
  }, [bundle?.bundleMasterId]);

  return (
    <>
      <br />

      <HBox align="flex-start">
        <VBox align="flex-start">
          <FieldValue label="Patient" wrap={true} value={`${patient?.lastName}, ${patient?.firstName}`} itemStyle={{ width: '160px' }} />
          <FieldValue label="Gender" value={patient?.gender || ''} />
          <FieldValue label="Date of Birth" value={patient?.birthDate ? formatLocalDate(patient?.birthDate) : ''} />
          <FieldValue label="Location" value={locationCode} />
          <FieldValue label="Account" value={accountNumber} />
          <FieldValue label="MRN" value={mrn} />
          {summary && <FieldValue label="DOS" value={formatLocalDate(summary.serviceDate)} />}
          <Typography variant="body2" color="textSecondary">
            Library
          </Typography>
          <DocumentLibrary locationCode={locationCode} departmentCode={departmentCode} workflow={workflow} />
        </VBox>
        <Divider orientation="vertical" style={{ height: '88vh' }} flexItem />

        <div
          style={{
            width: '100%',
            height: '84vh',
            overflowX: 'hidden',
            overflowY: 'scroll',
          }}
        >
          {showDocs && <BundleView api={api} bundle={bundle} />}

          <Divider orientation="horizontal" />
          <br />
        </div>
      </HBox>
    </>
  );
}

type DocumentViewerPropes = {
  ref?: DocumentRef;
  visit?: Visit;
};

export function DocumentViewer({ ref, visit }: DocumentViewerPropes) {
  visit = documentApi2.data((s) => s.visit) || visit;
  const error = documentApi2.data((s) => s.error);
  const bundle = documentApi2.data((s) => s.bundle);

  useEffect(() => {
    if (!bundle) {
      documentApi2.getBundles(visit);
    }
    if (error) {
      showToast(error);
    }
  }, [error, bundle]);

  let content = <></>;
  try {
    content = DocumentRenderer({ ref });
  } catch (e) {
    log.error(e);
    alert('There was an error rendering the documents. Please refresh the account page.');
  }
  return visit && <Page drawerWidth={420} drawerOpen={true} mainHeader={<DocumentHeader />} main={content} drawer={<DocumentSideDrawer visit={visit} api={documentApi2} />} />;
}
export function DocumentsRoute2() {
  const visit = documentApi2.data((s) => s.visit) || getCurrentVisitSession(window.name);
  documentApi2.data.setState({ visit });
  clearDocuments.setState({ show: false });
  if (!visit) {
    return <></>;
  }

  return <DocumentViewer visit={visit} />;
}
